import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { NAV_ITEMS, PHONE_DISPLAY, PHONE_TEL, WHATSAPP_URL } from "@/lib/svenData";
import { useGoTo } from "@/lib/useGoTo";
import { StyleQuizTrigger } from "@/components/sven/StyleQuizModal";

const BOOK_ITEM = { label: "Book a Fitting", type: "anchor", id: "book" };

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const goTo = useGoTo();
  const solid = scrolled || location.pathname !== "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (item) => {
    setOpen(false);
    setTimeout(() => goTo(item), open ? 250 : 0);
  };

  const goHome = () => {
    setOpen(false);
    if (location.pathname !== "/") navigate("/");
    else window.scrollTo({ top: 0 });
  };

  return (
    <>
      <header
        data-testid="header-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          solid ? "bg-[#0b0b0c]/90 backdrop-blur-xl border-b border-white/10" : "bg-gradient-to-b from-black/60 to-transparent"
        }`}
      >
        <div className="max-w-8xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
          <button data-testid="header-logo" onClick={goHome} className="flex items-center gap-3">
            {/*<img src="/images/brand/sven-logo.jpg" alt="Sven Suits logo" className="h-10 w-10 object-cover rounded-sm" /> */}
            <span className="font-display text-[25px] tracking-[0.18em] text-white">SVEN SUITS
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV_ITEMS.map((l) => (
              <button
                key={l.label}
                data-testid={`nav-link-${(l.id || l.to).replace("/", "")}`}
                onClick={() => go(l)}
                className={`gold-underline font-mono-ov text-[12px] tracking-[0.2em] uppercase transition-colors whitespace-nowrap ${
                  l.type === "page" && location.pathname === l.to ? "text-[#dfcca4]" : "text-white/75 hover:text-white"
                }`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a data-testid="header-phone-link" href={PHONE_TEL} className="hidden md:flex items-center gap-2 text-white/80 hover:text-white transition-colors">
              <Phone size={14} className="text-[#c5a059]" />
              <span className="font-mono-ov text-[12px] tracking-wider whitespace-nowrap">{PHONE_DISPLAY}</span>
            </a>
            <a
              data-testid="header-whatsapp-cta"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp Sven Suits"
              className="flex items-center justify-center h-9 w-9 rounded-full border border-white/20 text-[#25D366] hover:bg-[#25D366] hover:text-black hover:border-[#25D366] transition-all"
            >
              <FaWhatsapp size={16} />
            </a>
            <button
              data-testid="header-book-fitting-cta"
              onClick={() => go(BOOK_ITEM)}
              className="hidden sm:block px-5 py-2 bg-[#c5a059] text-[#0b0b0c] font-mono-ov text-[12px] tracking-[0.15em] uppercase rounded-full hover:bg-[#dfcca4] transition-colors whitespace-nowrap"
            >
              Book an Appointment
            </button>
            <button            >
            < StyleQuizTrigger label="Take the Style Quiz" testId="hero-style-quiz-trigger" 
            data-testid="header-book-fitting-cta"
            className="hidden sm:block px-5 py-2 bg-[#c5a059] text-[#f0b0b0c] font-mono-ov text-[12px] tracking-[0.15em] uppercase rounded-full hover:bg-[#dfcca4] transition-colors whitespace-nowrap"
            />
            </button>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
              className="lg:hidden flex items-center justify-center h-9 w-9 text-white"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-nav-drawer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#0b0b0c]/98 backdrop-blur-xl flex flex-col justify-center px-8 lg:hidden"
          >
            <nav className="flex flex-col gap-5">
              {NAV_ITEMS.concat([BOOK_ITEM]).map((l, i) => (
                <motion.button
                  key={l.label}
                  data-testid={`mobile-nav-link-${(l.id || l.to).replace("/", "")}`}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  onClick={() => go(l)}
                  className="text-left font-display text-3xl text-white/90 hover:text-[#c5a059] transition-colors"
                >
                  {l.label}
                </motion.button>
              ))}
            </nav>
            <div className="mt-10 pt-8 border-t border-white/10 flex items-center gap-5">
              <a href={PHONE_TEL} className="flex items-center gap-2 text-white/70">
                <Phone size={15} className="text-[#c5a059]" />
                <span className="font-mono-ov text-xs">{PHONE_DISPLAY}</span>
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="text-[#25D366]">
                <FaWhatsapp size={20} />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
